import React, { FC, useRef, useState, useEffect } from 'react';
import styles from './New.module.scss';
import { SignUpSteps } from './SignUpNew';
import Button from 'components/shared/Button';
import { ReactComponent as IconResend } from 'images/resend.svg';
import supabase from 'sdk/api';
import { useTranslation } from 'react-i18next';

type Props = {
  code: string[];
  setCode: (code: string[]) => void;
  setStep: (step: SignUpSteps) => void;
  phone: string;
  setToken: (token: any) => void;
  email: string;
  name: string;
  token: any;
};

const Code: FC<Props> = ({
  code,
  setCode,
  setStep,
  phone,
  setToken,
  email,
  name,
  token,
}): JSX.Element => {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [shortTimer, setShortTimer] = useState<number>(15);
  const [longTimer, setLongTimer] = useState<number | null>(null);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [codeError, setCodeError] = useState<string>('');
  const { t } = useTranslation() as any;
  // Первый таймер на 5 секунд
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (shortTimer > 0) {
      interval = setInterval(() => {
        setShortTimer(prev => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [shortTimer]);

  // Второй таймер на 3 минуты
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (longTimer !== null && longTimer > 0) {
      interval = setInterval(() => {
        setLongTimer(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [longTimer]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

   const ANON_KEY =  process.env.REACT_APP_SUPABASE_ANON_KEY;
   
  const handleResendCode = async () => {
    try {
      await fetch('https://gibnkggiuxphkienkxwf.supabase.co/functions/v1/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
           Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({
          phone,
        }),
      });
      setShortTimer(15);
      setLongTimer(15);
      setCanResend(false);
    } catch (error) {
      console.log('Error while resending code: ', error);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value.length <= 1) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Автоматический переход к следующему инпуту
      if (value !== '' && index < 5) {
        inputs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const fullCode = code.join('');

  const confirmCode = async () => {
    const { data: storedCode } = await supabase
      .from('code_verification')
      .select('code')
      .eq('phone', phone)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (storedCode?.code === fullCode) {
      setStep(SignUpSteps.Password);
    } else {
      setCodeError(t('code_invalid'));
    }
  };

  useEffect(() => {
    if (codeError) {
      setCodeError('');
    }
  }, [fullCode]);

  return (
    <div className={styles.email}>
      <div className={styles.step_header}>{t('enter_text_please')}</div>
      <div className={styles.code}>
        <div className={styles.code_fields}>
          {[0, 1, 2, 3].map(index => (
            <div key={index} className={styles.code_fields_field}>
              <input
                ref={el => (inputs.current[index] = el)}
                type="text"
                maxLength={1}
                value={code[index]}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                style={{
                  border: `1px dashed ${codeError ? '#FF3B30' : '#01010180'}`,
                  borderRadius: '100px',
                }}
              />
            </div>
          ))}
        </div>
        <div className={styles.code_resend}>
          {!codeError && (
            <div className={styles.code_resend_info}>
              {t('code_send_to_phone_number')} <span>+{phone}</span>
            </div>
          )}
          <div className={styles.code_resend_timer}>
            {shortTimer > 0 && !longTimer && (
              <>
                {t('code_resend_seconds')} <span>{shortTimer}s</span>
              </>
            )}
          </div>
        </div>
        {codeError && <div className={styles.error}>{codeError}</div>}
      </div>
      {longTimer !== null && longTimer > 0 && (
        <div className={styles.code_resend_timer}>
          <>
            {t('code_resend_seconds')} <span>{formatTime(longTimer)}</span>
          </>
        </div>
      )}
      <div className={styles.code_buttons}>
        {canResend && !longTimer ? (
          <Button
            className={styles.resendButton}
            black={false}
            wide
            onClick={handleResendCode}
            yellow
          >
            <IconResend />
            {t('resend_code')}
          </Button>
        ) : (
          <div style={{ width: '100%' }} />
        )}
        <Button
          className={styles.resendButton}
          black={false}
          style={{ width: '60px' }}
          onClick={confirmCode}
          darkGreen
          disabled={!fullCode}
        >
          {t('next')}
        </Button>
      </div>
    </div>
  );
};

export default Code;
